import { apiClient } from '@/api/client'
import {
  companyLeavesPath,
  companyLeaveByIdPath,
  companyLeaveDecisionPath,
  companyLeaveReplacementPath,
  LEAVES,
  LEAVES_ME,
} from '@/api/endpoints'
import type { ApiResponseWrapper } from '@/types/api.types'
import type {
  LeaveDocument,
  LeaveDocumentWithHandoff,
  CreateLeaveRequestDto,
  UpdateLeaveRequestDto,
  LeaveDecisionDto,
  AssignReplacementDto,
  MyLeaveListQuery,
  MyLeaveListResponse,
} from '@/features/leave/types/leave.types'

export async function createLeave(
  companyId: string,
  body: CreateLeaveRequestDto
): Promise<LeaveDocument> {
  const { data } = await apiClient.post<ApiResponseWrapper<LeaveDocument>>(
    companyLeavesPath(companyId),
    body
  )
  const doc = data?.data
  if (!doc?._id) throw new Error('Invalid create leave response')
  return doc
}

export async function getLeaveById(companyId: string, leaveId: string): Promise<LeaveDocument> {
  const { data } = await apiClient.get<ApiResponseWrapper<LeaveDocument>>(
    companyLeaveByIdPath(companyId, leaveId)
  )
  const doc = data?.data
  if (!doc?._id) throw new Error('Leave request not found')
  return doc
}

export async function updateLeave(
  companyId: string,
  leaveId: string,
  body: UpdateLeaveRequestDto
): Promise<LeaveDocument> {
  const { data } = await apiClient.patch<ApiResponseWrapper<LeaveDocument>>(
    companyLeaveByIdPath(companyId, leaveId),
    body
  )
  const doc = data?.data
  if (!doc?._id) throw new Error('Invalid update leave response')
  return doc
}

export async function postLeaveDecision(
  companyId: string,
  leaveId: string,
  body: LeaveDecisionDto
): Promise<LeaveDocument> {
  const { data } = await apiClient.post<ApiResponseWrapper<LeaveDocument>>(
    companyLeaveDecisionPath(companyId, leaveId),
    body
  )
  const doc = data?.data
  if (!doc?._id) throw new Error('Invalid decision response')
  return doc
}

export async function postLeaveReplacement(
  companyId: string,
  leaveId: string,
  body: AssignReplacementDto
): Promise<LeaveDocumentWithHandoff> {
  const { data } = await apiClient.post<ApiResponseWrapper<LeaveDocumentWithHandoff>>(
    companyLeaveReplacementPath(companyId, leaveId),
    body
  )
  const doc = data?.data
  if (!doc?._id) throw new Error('Invalid replacement response')
  return doc
}

/** Operator workspace: cross-company leaves. Backend intersects companyIds with caller's memberships. */
export async function getLeavesWorkspace(query?: MyLeaveListQuery): Promise<MyLeaveListResponse> {
  const { companyIds, ...rest } = query ?? {}
  const params: Record<string, unknown> = { ...rest }
  if (companyIds && companyIds.length > 0) {
    params.companyIds = companyIds.join(',')
  }
  const { data } = await apiClient.get<ApiResponseWrapper<MyLeaveListResponse>>(LEAVES, { params })
  const payload = data?.data
  if (payload && Array.isArray(payload.data)) return payload
  return { data: [], pagination: { total: 0, page: 1, limit: query?.limit ?? 10 } }
}

/** Cross-company self-service leaves: backend auto-scopes to caller's memberships. */
export async function getMyLeaves(query?: MyLeaveListQuery): Promise<MyLeaveListResponse> {
  const { companyIds, ...rest } = query ?? {}
  const params: Record<string, unknown> = { ...rest }
  if (companyIds && companyIds.length > 0) {
    params.companyIds = companyIds.join(',')
  }
  const { data } = await apiClient.get<ApiResponseWrapper<MyLeaveListResponse>>(LEAVES_ME, {
    params,
  })
  const payload = data?.data
  if (payload && Array.isArray(payload.data)) return payload
  return { data: [], pagination: { total: 0, page: 1, limit: query?.limit ?? 10 } }
}
